import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { contactsApi } from '../api/contacts-api';
import { fieldWorkApi } from '../api/field-work-api';
import { fulfillmentApi } from '../api/fulfillment-api';
import { shipmentsApi } from '../api/shipments-api';
import { ErrorState } from '../components/ErrorState';
import { PageLoadingState } from '../components/PageLoadingState';
import { formatSiteAddress } from '../domain/siteContact';
import { siteReceiver } from '../features/contacts/site-receiver';
import { packingListRows, shortRows } from '../features/fulfillment/packing-list';
import { useFulfillmentReferences } from '../features/fulfillment/use-fulfillment-references';

/**
 * One printable sheet per order for the pallet: what is packed, with
 * serial numbers, so the customer or technician can check it without a
 * computer (tidaltech/koi#8205). The app shell is hidden when printing.
 */
export function PackingListPage() {
  const { orderId = '' } = useParams();
  const references = useFulfillmentReferences();
  const order = useQuery({
    queryKey: ['fulfillment-order', orderId],
    queryFn: () => fulfillmentApi.getOrder(orderId),
  });
  const shipmentId = order.data?.shipmentId;
  const shipment = useQuery({
    queryKey: ['shipment', shipmentId],
    queryFn: () => shipmentsApi.getShipment(shipmentId ?? ''),
    enabled: Boolean(shipmentId),
  });
  const requestId = order.data?.fieldWorkRequestId;
  const request = useQuery({
    queryKey: ['field-work', requestId],
    queryFn: () => fieldWorkApi.getFieldWork(requestId ?? ''),
    enabled: Boolean(requestId),
  });
  const siteId = order.data?.siteId;
  const siteContacts = useQuery({
    queryKey: ['contacts', 'resolve', siteId],
    queryFn: () => contactsApi.resolveContacts(siteId ?? ''),
    enabled: Boolean(siteId),
  });

  if (order.isLoading) return <PageLoadingState label="Loading packing list…" />;
  if (order.isError || !order.data) {
    return (
      <ErrorState
        title="The packing list could not be loaded"
        rpcName="GetFulfillmentOrder"
        onRetry={() => void order.refetch()}
      />
    );
  }

  const current = order.data;
  const site = references.sites.find((candidate) => candidate.id === current.siteId);
  const address = formatSiteAddress(site?.address);
  const receiver = siteContacts.data ? siteReceiver(siteContacts.data) : undefined;
  const rows = packingListRows(current, {
    part: references.partName,
    stock: references.stockName,
    unit: references.unit,
  });
  const short = shortRows(rows);

  return (
    <div className="packing-list">
      <div className="packing-list-toolbar no-print">
        <Link to={`/fulfillment/${current.id}`}>← Back to the order</Link>
        <button type="button" className="primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <article className="packing-list-sheet" aria-label="Packing list">
        <header className="packing-list-header">
          <h1>Packing list</h1>
          <p className="packing-list-order">
            {current.externalReference || `Order ${current.id.slice(0, 8)}`}
          </p>
        </header>

        <dl className="packing-list-meta">
          <div>
            <dt>Customer</dt>
            <dd>{references.customerName(current.customerId)}</dd>
          </div>
          <div>
            <dt>Site</dt>
            <dd>
              {references.siteName(current.siteId)}
              {receiver ? <span>Contact: {receiver.name}</span> : null}
              {address ? <span>{address}</span> : null}
            </dd>
          </div>
          {current.dispatchDate ? (
            <div>
              <dt>Dispatch</dt>
              <dd>{new Date(current.dispatchDate).toLocaleDateString()}</dd>
            </div>
          ) : null}
          {shipment.data ? (
            <div>
              <dt>Consignment</dt>
              <dd>
                {shipment.data.carrier} {shipment.data.cargonizerConsignmentNumber}
              </dd>
            </div>
          ) : null}
          {requestId ? (
            <div>
              <dt>Field work</dt>
              <dd>{request.data?.title || requestId}</dd>
            </div>
          ) : null}
        </dl>

        {short.length > 0 ? (
          <p className="warning-callout" role="status">
            Not fully packed: {short.length} of {rows.length}{' '}
            {rows.length === 1 ? 'line is' : 'lines are'} short. The quantities below are what is
            packed now.
          </p>
        ) : null}

        <table className="packing-list-lines">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Quantity</th>
              <th scope="col">Serial numbers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.lineId}>
                <th scope="row">{row.name}</th>
                <td className="mono">
                  {row.packed}
                  {row.unit ? ` ${row.unit}` : ''}
                  {row.packed < row.ordered ? ` of ${row.ordered}` : ''}
                </td>
                <td className="mono packing-list-serials">
                  {row.serials.length > 0 ? row.serials.join(', ') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {current.notes ? (
          <section className="packing-list-notes">
            <h2>Notes</h2>
            <p>{current.notes}</p>
          </section>
        ) : null}

        <footer className="packing-list-footer">
          Printed {new Date().toLocaleString()}
          {current.handedOverBy ? ` · Packed by ${current.handedOverBy}` : ''}
        </footer>
      </article>
    </div>
  );
}
